import axiosInstance from "@/utils/axiosInstance";

export interface GovernanceFieldMetadata {
  pii_level: string;
  finance_sensitive: boolean;
  duplicate_check_field: boolean;
  required_for_diligence: boolean;
  discrepancy_trackable: boolean;
  visible_to_client: string;
  required_for_disbursal: boolean;
  required_for_interview: boolean;
  required_for_mentorship: boolean;
  ocr_candidate: boolean;
  ocr_allowed: boolean;
  governance_required: boolean;
  analytics_dimension?: string | null;
}

export interface CanonicalField {
  field_key: string;
  label: string;
  description: string;
  field_type: string;
  category: string;
  governance_domain: string;
  active: boolean;
  lifecycle_state: string;
  replacement_field_key?: string | null;
  migration_guidance?: string | null;
  owner_domains: string[];
  dependency_field_keys: string[];
  dependency_severity: Record<string, string>;
  compatible_field_keys: string[];
  workflow_requirement_domains: string[];
  version: string;
  aliases: string[];
  metadata: GovernanceFieldMetadata;
}

export interface FormGovernanceWarning {
  warning_id: string;
  severity: string;
  layer: string;
  title: string;
  description: string;
  recommended_action: string;
}

export interface FormPublishability {
  publishable: boolean;
  verdict: string;
  blocking_issue_count: number;
  warning_issue_count: number;
  blocking_warning_ids: string[];
  warning_ids: string[];
  required_checks: string[];
}

export interface FormGovernanceLifecycle {
  state: string;
  label: string;
  description: string;
  editable: boolean;
  intake_enabled: boolean;
  requires_governance_review: boolean;
  terminal: boolean;
  allowed_next_states: string[];
  publish_authority_roles: string[];
  draft_authority_roles: string[];
  audit_events_required: string[];
  source: string;
}

export interface FormGovernanceValidationReport {
  form_id: number;
  tenant_id: number;
  name: string;
  version: string;
  program_code?: string | null;
  active: boolean;
  warnings: FormGovernanceWarning[];
  publishability: FormPublishability;
  lifecycle: FormGovernanceLifecycle;
  metadata: Record<string, unknown>;
}

export interface FormGovernanceRegistry {
  tenant_id: number;
  read_only: boolean;
  registry_version: string;
  canonical_fields: CanonicalField[];
  forms: FormGovernanceValidationReport[];
  warnings: FormGovernanceWarning[];
  metadata: Record<string, unknown>;
}

export interface FormDraftSection {
  section_key: string;
  title: string;
  field_keys: string[];
}

export interface FormDraftValidation {
  warnings: FormGovernanceWarning[];
  publishability: FormPublishability;
}

export interface PMReviewProcessOption {
  id: number;
  name: string;
  version: number;
  status: string;
  summary?: string | null;
  path_label?: string | null;
  available_for_selection: boolean;
  blocked_reason?: string | null;
}

export interface FormDraftSelectedReviewProcess {
  id: number;
  workflow_template_id: number;
  name: string;
  version: number;
  status: string;
  summary?: string | null;
  selected_by_user_id: number;
  selected_at?: string | null;
  updated_at?: string | null;
}

export interface FormDraftDiff {
  source: string;
  source_form_id?: number | null;
  source_form_version_id?: number | null;
  fields_added: string[];
  fields_removed: string[];
  fields_retained: string[];
  field_order_changed: boolean;
  sections_added: string[];
  sections_removed: string[];
  section_order_changed: boolean;
  governance_metadata_impact: Record<string, unknown>[];
  workflow_requirement_domain_delta: string[];
  publishability_delta: Record<string, unknown>;
  risk_level: string;
  risk_reasons: string[];
  governance_review_severity: string;
  severity_reasons: string[];
  reviewer_action_required: string;
  approval_authority: {
    severity?: string;
    required_review_domains?: string[];
    participating_reviewer_roles?: string[];
    final_approval_roles?: string[];
    required_supporting_roles?: string[];
    pm_self_publish_blocked?: boolean;
    activation_blocked_in_current_slice?: boolean;
    doctrine?: string;
  };
}

export interface FormDraftProjection {
  id: number;
  tenant_id: number;
  source_student_form_id?: number | null;
  created_by_user_id: number;
  name: string;
  program_code: string;
  academic_year: string;
  status: string;
  sections: FormDraftSection[];
  schema_snapshot: Record<string, unknown>;
  validation: FormDraftValidation;
  diff: FormDraftDiff;
  applicant_visible: boolean;
  publish_blocked: boolean;
  selected_review_process?: FormDraftSelectedReviewProcess | null;
  created_at?: string | null;
  updated_at?: string | null;
  governance_constraints: Record<string, unknown>;
}

export interface FormApprovalEvidenceSnapshot {
  id: number;
  tenant_id: number;
  form_draft_id: number;
  submitted_by_user_id: number;
  submitted_by_role: string;
  status: string;
  sections_snapshot: FormDraftSection[];
  schema_snapshot: Record<string, unknown>;
  diff_snapshot: FormDraftDiff;
  validation_snapshot: FormDraftValidation;
  approval_authority_snapshot: FormDraftDiff["approval_authority"];
  governance_review_severity: string;
  required_review_domains: string[];
  final_approval_roles: string[];
  required_supporting_roles: string[];
  activation_blocked: boolean;
  applicant_visible: boolean;
  created_at?: string | null;
  governance_constraints: Record<string, unknown>;
}

export interface FormApprovalConcurrenceEvent {
  id: number;
  actor_id: number;
  actor_role: string;
  concurrence_domain: string;
  decision: string;
  remarks?: string | null;
  created_at?: string | null;
}

export interface FormApprovalConcurrenceState {
  approval_evidence_snapshot_id: number;
  tenant_id: number;
  governance_review_severity: string;
  required_review_domains: string[];
  final_approval_roles: string[];
  required_supporting_roles: string[];
  role_states: Record<string, unknown>[];
  domain_states: Record<string, unknown>[];
  latest_events: FormApprovalConcurrenceEvent[];
  final_approval_satisfied: boolean;
  supporting_approval_satisfied: boolean;
  rejected: boolean;
  activation_preconditions_satisfied: boolean;
  activation_blocked: boolean;
  applicant_visible: boolean;
  governance_constraints: Record<string, unknown>;
}

export interface FormDraftCreatePayload {
  program_code: string;
  academic_year: string;
  name?: string;
  source_student_form_id?: number | null;
  sections?: FormDraftSection[];
}

export interface FormDraftSectionsUpdatePayload {
  sections: FormDraftSection[];
}

export interface FormDraftReviewProcessSelectionPayload {
  workflow_template_id: number;
}

export async function fetchPMFormGovernanceRegistry(): Promise<FormGovernanceRegistry> {
  const res = await axiosInstance.get<FormGovernanceRegistry>(
    "/pm/form-governance-registry"
  );
  return res.data;
}

export async function fetchPMFormDrafts(): Promise<FormDraftProjection[]> {
  const res = await axiosInstance.get<FormDraftProjection[]>("/pm/form-drafts");
  return res.data;
}

export async function fetchPMReviewProcessOptions(
  params?: { program_code?: string; academic_year?: string }
): Promise<PMReviewProcessOption[]> {
  const res = await axiosInstance.get<PMReviewProcessOption[]>(
    "/pm/review-process-options",
    { params }
  );
  return res.data;
}

export async function createPMFormDraft(
  payload: FormDraftCreatePayload
): Promise<FormDraftProjection> {
  const res = await axiosInstance.post<FormDraftProjection>("/pm/form-drafts", payload);
  return res.data;
}

export async function updatePMFormDraftSections(
  draftId: number,
  payload: FormDraftSectionsUpdatePayload
): Promise<FormDraftProjection> {
  const res = await axiosInstance.put<FormDraftProjection>(
    `/pm/form-drafts/${draftId}/sections`,
    payload
  );
  return res.data;
}

export async function savePMFormDraftReviewProcessSelection(
  draftId: number,
  payload: FormDraftReviewProcessSelectionPayload
): Promise<FormDraftProjection> {
  const res = await axiosInstance.put<FormDraftProjection>(
    `/pm/form-drafts/${draftId}/review-process-selection`,
    payload
  );
  return res.data;
}

export async function createPMFormDraftReviewSnapshot(
  draftId: number
): Promise<FormApprovalEvidenceSnapshot> {
  const res = await axiosInstance.post<FormApprovalEvidenceSnapshot>(
    `/pm/form-drafts/${draftId}/review-snapshots`
  );
  return res.data;
}

export async function fetchFormApprovalConcurrence(
  snapshotId: number
): Promise<FormApprovalConcurrenceState> {
  const res = await axiosInstance.get<FormApprovalConcurrenceState>(
    `/form-governance/approval-snapshots/${snapshotId}/concurrence`
  );
  return res.data;
}
